// Header "who's online" roster, and the panel behind it.
//
// The stack of avatars answers "is anyone else here". It cannot answer the
// question that actually matters once a team covers several sites — "is anyone
// on NICGulf right now?" — because an avatar carries no site. So the stack is
// a trigger, and the panel groups the same people by site with a count each.
//
// Sites are identified by their own tinted badge rather than an uploaded
// logo, which no site has: the accent/text colours already stand for a site
// everywhere else in this app (monitor rows, chat history, map pins), so
// reusing them means the colour an agent learned in one place holds here too.

import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useAgentStore } from '../stores/agent';
import { usePresenceStore } from '../stores/presence';
import { useSiteNames, siteBadgeStyle, type SiteLite } from '../stores/sites';
import { resolveAvatarUrl } from '../lib/server-origin';
import styles from './AgentPresence.module.css';

const MAX_SHOWN = 5;

function isAdmin(role?: string): boolean {
  return role === 'site-admin' || role === 'account-admin' || role === 'super-admin';
}

interface Person {
  agentId: string;
  name: string;
  initials: string;
  avatarUrl?: string;
  role?: string;
  availability?: string;
  siteIds: Set<string>;
  self: boolean;
}

export function AgentPresence() {
  const me = useAgentStore((s) => s.current);
  const myAvailability = useAgentStore((s) => s.availability);
  // Select the stable Map reference (it's replaced only on a real presence
  // change) and derive the array here — selecting a freshly-built array would
  // change identity every render and loop useSyncExternalStore.
  const othersMap = usePresenceStore((s) => s.others);
  const { byId: siteById } = useSiteNames();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Dismiss on an outside click or Escape. A header popover that survives
  // either is a popover the agent has to hunt for a way to close.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const others = useMemo(() => Array.from(othersMap.values()), [othersMap]);

  // The sites the current agent can see are exactly the sites they belong to
  // (the endpoint scopes it), so that map doubles as "my sites" for self.
  const mySiteIds = useMemo(() => new Set(siteById.keys()), [siteById]);

  const people: Person[] = useMemo(() => {
    if (!me) return [];
    return [
      {
        agentId: me.agentId,
        name: me.name,
        initials: me.initials,
        ...(me.avatarUrl ? { avatarUrl: me.avatarUrl } : {}),
        role: me.role,
        availability: myAvailability,
        siteIds: mySiteIds,
        self: true,
      },
      ...others.map((o) => ({
        agentId: o.agentId as string,
        name: o.name,
        initials: o.initials,
        ...(o.avatarUrl ? { avatarUrl: o.avatarUrl } : {}),
        role: o.role,
        availability: o.availability,
        siteIds: o.siteIds,
        self: false,
      })),
    ];
  }, [me, myAvailability, mySiteIds, others]);

  // One group per site that has anybody on it, busiest first. An agent on
  // three sites appears under all three — they genuinely are covering all
  // three, and showing them once would understate every site but one.
  const groups = useMemo(() => {
    const bySite = new Map<string, Person[]>();
    for (const person of people) {
      for (const siteId of person.siteIds) {
        const list = bySite.get(siteId);
        if (list) list.push(person);
        else bySite.set(siteId, [person]);
      }
    }
    return [...bySite.entries()]
      .map(([siteId, members]) => ({
        siteId,
        site: siteById.get(siteId),
        name: siteById.get(siteId)?.name ?? 'Unknown site',
        members: members.sort((a, b) => Number(b.self) - Number(a.self) || a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => b.members.length - a.members.length || a.name.localeCompare(b.name));
  }, [people, siteById]);

  // Agents we know of who are on no site we can see. Never silently dropped —
  // an invisible colleague is worse than an oddly-filed one.
  const unassigned = useMemo(
    () => people.filter((p) => [...p.siteIds].every((id) => !siteById.has(id))),
    [people, siteById],
  );

  if (!me) return null;

  const shown = people.slice(0, MAX_SHOWN);
  const overflow = people.length - shown.length;
  const othersCount = others.length;

  return (
    <div className={styles.roster} ref={rootRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={
          othersCount === 0
            ? 'No other agents online'
            : `${othersCount} other agent${othersCount === 1 ? '' : 's'} online — by site`
        }
      >
        <ul className={styles.stack}>
          {shown.map((p) => (
            <li key={p.agentId} className={styles.item}>
              <span
                className={[
                  styles.avatar,
                  p.self ? styles.self : '',
                  isAdmin(p.role) ? styles.admin : '',
                ].join(' ')}
              >
                {p.avatarUrl ? (
                  <img className={styles.img} src={resolveAvatarUrl(p.avatarUrl)} alt="" aria-hidden />
                ) : (
                  p.initials
                )}
              </span>
            </li>
          ))}
          {overflow > 0 ? (
            <li className={styles.item}>
              <span className={[styles.avatar, styles.more].join(' ')}>+{overflow}</span>
            </li>
          ) : null}
        </ul>
        <span className={styles.count}>{othersCount} online</span>
        <ChevronDown className={styles.chevron} size={13} aria-hidden data-open={open} />
      </button>

      {open ? (
        <div className={styles.panel} role="dialog" aria-label="Agents online by site">
          <p className={styles.panelHead}>
            Online by site
            <span className={styles.panelTotal}>{people.length}</span>
          </p>
          {groups.length === 0 ? (
            <p className={styles.empty}>No sites to report on yet.</p>
          ) : (
            <ul className={styles.groups}>
              {groups.map((group) => (
                <li key={group.siteId} className={styles.group}>
                  <div className={styles.groupHead}>
                    <SiteIcon site={group.site} name={group.name} />
                    <span className={styles.groupName}>{group.name}</span>
                    <span className={styles.groupCount}>{group.members.length}</span>
                  </div>
                  <ul className={styles.members}>
                    {group.members.map((p) => (
                      <li key={p.agentId} className={styles.member}>
                        <span
                          className={[
                            styles.memberAvatar,
                            isAdmin(p.role) ? styles.admin : '',
                          ].join(' ')}
                        >
                          {p.avatarUrl ? (
                            <img
                              className={styles.img}
                              src={resolveAvatarUrl(p.avatarUrl)}
                              alt=""
                              aria-hidden
                            />
                          ) : (
                            p.initials
                          )}
                        </span>
                        <span className={styles.memberName}>
                          {p.name}
                          {p.self ? <span className={styles.you}>you</span> : null}
                        </span>
                        <span
                          className={styles.availability}
                          data-availability={p.availability ?? 'available'}
                          title={p.availability ?? 'available'}
                        >
                          {p.availability ?? 'available'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
          {unassigned.length > 0 ? (
            <p className={styles.note}>
              {unassigned.length} online on a site you don&rsquo;t have access to.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** The site's own colours as a square chip — the same identity used elsewhere. */
function SiteIcon({ site, name }: { site?: SiteLite; name: string }) {
  return (
    <span className={styles.siteIcon} style={siteBadgeStyle(site)} aria-hidden>
      {name.slice(0, 1).toUpperCase()}
    </span>
  );
}
